import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../../components/PageHeader';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useModuleLicense } from '../../../hooks/useModuleLicense';
import pbrService from '../../../services/pbr';
import PbrStageBadge from '../../../components/cultivar/pbr/PbrStageBadge';
import DeadlineChip from '../../../components/cultivar/pbr/DeadlineChip';
import type {
  PbrApplication, PbrApplicationEvent, QualifiedPerson,
  PbrStage, ComplianceDocument,
} from '../../../types/cultivar';
import { PBR_STAGE_LABELS } from '../../../types/cultivar';
import {
  FileCheck, Save, Download, Send, ShieldAlert,
  FlaskConical, AlertCircle, CheckCircle2, Search, Loader2,
} from 'lucide-react';

const QP_FUNCTION_LABELS: Record<string, string> = {
  review_overseas_docs: 'Review application documents first filed in another UPOV member country',
  perform_dus_functions: 'Perform functions if a comparative DUS test growing is required',
  completion_part1: 'Completion of Part 1 of the application form',
  determine_similar_varieties: 'Determine the most similar varieties of common knowledge',
  plan_trial: 'Planning the test growing trial',
  recommend_trial_site: 'Recommending the most appropriate trial site',
  choice_trial_site: 'Choice of trial site',
  supervise_layout: 'Supervision of the layout and planting of the trial',
  care_trial: 'Care and maintenance of the trial',
  instruct_observations: 'Instruction on timing and nature of observations',
  certification_part2: 'Certification of the Part 2 application form',
  completion_part2: 'Completion of Part 2 of the PBR application',
  verification_trial: 'Verification of the field trial, observations, data and statistical analysis',
  provide_photo: 'Provide a comparative slide or a colour print of the variety',
};

// Which transitions the UI offers — the server enforces validity
const NEXT_STAGES: Record<PbrStage, PbrStage[]> = {
  candidate: ['part1_prep', 'withdrawn'],
  part1_prep: ['part1_submitted', 'withdrawn'],
  part1_submitted: ['accepted', 'trial_in_progress', 'rejected', 'withdrawn'],
  accepted: ['trial_in_progress', 'part2_prep', 'withdrawn'],
  trial_in_progress: ['part2_prep', 'withdrawn'],
  part2_prep: ['part2_submitted', 'withdrawn'],
  part2_submitted: ['granted', 'rejected', 'withdrawn'],
  granted: [],
  ceased: [],
  rejected: [],
  withdrawn: [],
};

export default function PbrApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const appId = Number(id);
  const { hasAccess, loading: licLoading } = useModuleLicense('cultivar');

  const [app, setApp] = useState<PbrApplication | null>(null);
  const [events, setEvents] = useState<PbrApplicationEvent[]>([]);
  const [docs, setDocs] = useState<ComplianceDocument[]>([]);
  const [qps, setQps] = useState<QualifiedPerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [lookupRunning, setLookupRunning] = useState(false);
  const [lookupSummary, setLookupSummary] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!hasAccess) { setLoading(false); return; }
    setLoading(true);
    const [a, ev, d, q] = await Promise.all([
      pbrService.getApplication(appId),
      pbrService.listEvents(appId),
      pbrService.listDocuments(appId),
      pbrService.listQualifiedPersons({ activeOnly: true }),
    ]);
    if (a.success && a.data) setApp(a.data);
    if (ev.success && ev.data) setEvents(ev.data);
    if (d.success && d.data) setDocs(d.data);
    if (q.success && q.data) setQps(q.data);
    setLoading(false);
  }, [appId, hasAccess]);

  useEffect(() => { load(); }, [load]);

  if (licLoading) return <div className="p-6 text-neutral-500">Loading…</div>;
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-neutral-700">Access Restricted</h2>
        </div>
      </div>
    );
  }
  if (loading) return <div className="p-6 text-neutral-500">Loading…</div>;
  if (!app) return <div className="p-6 text-red-600">Application not found.</div>;

  const patch = async (updates: Partial<PbrApplication>) => {
    setSaving(true);
    setError(null);
    try {
      const r = await pbrService.updateApplication(appId, updates);
      if (r.success && r.data) { setApp(r.data); setFlash('Saved'); setTimeout(() => setFlash(null), 1500); }
      else setError(r.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const transition = async (newStage: PbrStage) => {
    const r = await pbrService.transitionStage(appId, newStage);
    if (r.success) { await load(); setFlash(`Stage → ${PBR_STAGE_LABELS[newStage]}`); setTimeout(() => setFlash(null), 2000); }
    else setError(r.message || 'Transition failed');
  };

  const generate = async (kind: 'part1' | 'nomination' | 'authorisation') => {
    setGenerating(kind);
    const fn = kind === 'part1' ? pbrService.generatePart1.bind(pbrService)
            : kind === 'nomination' ? pbrService.generateNominationOfQp.bind(pbrService)
            : pbrService.generateAuthorisationOfAgent.bind(pbrService);
    try {
      const r = await fn(appId);
      if (r.success && r.data) {
        setFlash('PDF generated and emailed');
        await load();
      } else setError(r.message || 'Generation failed');
    } finally { setGenerating(null); }
  };

  const toggleQpFunction = (key: string) => {
    const current = app.qp_nomination_functions || {};
    patch({ qp_nomination_functions: { ...current, [key]: !current[key] } });
  };

  const runLookup = async () => {
    if (!confirm(
      'Run a PBR lookup for this cultivar? This searches public registries (IP Australia, UPOV, USPTO, CPVO, NZ PVR) using the Anthropic API and updates protections automatically. Existing protection records are updated in place; cultivar fields are only written where currently blank.'
    )) return;
    setLookupRunning(true);
    setLookupSummary(null);
    setError(null);
    try {
      const r = await pbrService.runCultivarLookup(app.cultivar_id);
      if (r.success && r.data) {
        const np = r.data.applied.protections.length;
        const nc = Object.keys(r.data.applied.cultivar_updates).length;
        setLookupSummary(
          `Applied ${np} protection record${np === 1 ? '' : 's'}${nc > 0 ? ` and ${nc} cultivar field update${nc === 1 ? '' : 's'}` : ''}.${r.data.warning ? ` Warning: ${r.data.warning}` : ''}`
        );
        await load();
      } else {
        setError(r.message || 'Lookup failed');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Lookup failed');
    } finally {
      setLookupRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={app.cultivar_trade_name || app.cultivar_name || 'PBR Application'}
        subtitle={`${app.jurisdiction} · ${app.application_type.toUpperCase()}`}
        icon={<FileCheck className="h-6 w-6 text-white" />}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Header strip */}
        <div className="bg-white border border-neutral-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <PbrStageBadge stage={app.stage} />
            <DeadlineChip deadline={app.part1_due_date} />
            {app.registration_number && (
              <span className="text-sm text-neutral-500">#{app.registration_number}</span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {NEXT_STAGES[app.stage].map(s => (
              <Button key={s} size="sm" variant="ghost" onClick={() => transition(s)}>
                → {PBR_STAGE_LABELS[s]}
              </Button>
            ))}
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded">{error}</div>}
        {flash && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm p-3 rounded">{flash}</div>}

        {/* Automated registry lookup */}
        <section className="bg-primary-50 border border-primary-200 rounded-xl p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-medium text-primary-800">Automated registry lookup</div>
              <p className="text-sm text-primary-700 mt-1">
                Search IP Australia, UPOV PLUTO, USPTO, CPVO and NZ PVR for registrations matching
                this cultivar and apply any findings to the Protection tab. Restricted to cultivar
                admins and the cultivar's creator organisation.
              </p>
            </div>
            <Button size="sm" onClick={runLookup} disabled={lookupRunning}>
              {lookupRunning
                ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Searching…</>
                : <><Search className="h-4 w-4 mr-1" /> Run PBR lookup</>}
            </Button>
          </div>
          {lookupSummary && (
            <div className="mt-3 text-sm bg-white rounded p-2 border border-primary-100 text-primary-900">
              {lookupSummary}
            </div>
          )}
        </section>

        {/* Applicant + Agent */}
        <section className="bg-white border border-neutral-200 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold">Applicant & Agent</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Applicant name" defaultValue={app.applicant_name || ''} onBlur={e => e.target.value !== (app.applicant_name || '') && patch({ applicant_name: e.target.value })} />
            <Input label="Applicant country" defaultValue={app.applicant_country || ''} onBlur={e => e.target.value !== (app.applicant_country || '') && patch({ applicant_country: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input label="State" defaultValue={app.applicant_state || ''} onBlur={e => patch({ applicant_state: e.target.value })} />
            <Input label="Postcode" defaultValue={app.applicant_postcode || ''} onBlur={e => patch({ applicant_postcode: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-neutral-700 mb-1">Applicant address</label>
            <textarea
              defaultValue={app.applicant_address || ''}
              onBlur={e => patch({ applicant_address: e.target.value })}
              rows={2}
              className="input-professional w-full"
            />
          </div>
          <div className="border-t border-neutral-100 pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Agent name" defaultValue={app.agent_name || ''} onBlur={e => patch({ agent_name: e.target.value })} />
            <Input label="Agent contact person" defaultValue={app.agent_contact_person || ''} onBlur={e => patch({ agent_contact_person: e.target.value })} />
            <Input label="Agent phone" defaultValue={app.agent_phone || ''} onBlur={e => patch({ agent_phone: e.target.value })} />
            <Input label="Agent email" type="email" defaultValue={app.agent_email || ''} onBlur={e => patch({ agent_email: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-neutral-700 mb-1">Agent address</label>
            <textarea
              defaultValue={app.agent_address || ''}
              onBlur={e => patch({ agent_address: e.target.value })}
              rows={2}
              className="input-professional w-full"
            />
          </div>
          <div className="pt-2">
            <Button size="sm" variant="ghost" onClick={() => generate('authorisation')} disabled={generating === 'authorisation' || saving}>
              <Download className="h-4 w-4 mr-1" />
              {generating === 'authorisation' ? 'Generating…' : 'Generate Authorisation of Agent PDF'}
            </Button>
          </div>
        </section>

        {/* Qualified Person */}
        <section className="bg-white border border-neutral-200 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold">Qualified Person</h2>
          <div>
            <label className="block text-sm font-semibold text-neutral-700 mb-1">Nominated QP</label>
            <select
              value={app.qualified_person_id || ''}
              onChange={e => patch({ qualified_person_id: e.target.value ? Number(e.target.value) : null })}
              className="input-professional w-full"
            >
              <option value="">— None selected —</option>
              {qps.map(qp => (
                <option key={qp.id} value={qp.id}>
                  {qp.full_name}{qp.accreditation_number ? ` (${qp.accreditation_number})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="pt-2">
            <div className="text-sm font-semibold text-neutral-700 mb-2">Functions the QP will perform</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {Object.entries(QP_FUNCTION_LABELS).map(([k, label]) => (
                <label key={k} className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!(app.qp_nomination_functions || {})[k]}
                    onChange={() => toggleQpFunction(k)}
                    className="mt-0.5"
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="pt-2">
            <Button size="sm" variant="ghost" onClick={() => generate('nomination')} disabled={generating === 'nomination' || saving || !app.qualified_person_id}>
              <Download className="h-4 w-4 mr-1" />
              {generating === 'nomination' ? 'Generating…' : 'Generate Nomination of QP PDF'}
            </Button>
            {!app.qualified_person_id && (
              <p className="text-xs text-neutral-500 mt-1">Select a QP above to enable PDF generation.</p>
            )}
          </div>
        </section>

        {/* Growing Trial */}
        <section className="bg-white border border-neutral-200 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FlaskConical className="h-5 w-5" /> Growing Trial
          </h2>
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={!!app.upov_description_available}
              onChange={e => patch({ upov_description_available: e.target.checked })}
              className="mt-0.5"
            />
            <div>
              <div className="font-medium">UPOV description is published</div>
              <div className="text-xs text-neutral-500">A published overseas description (e.g. UK / UPOV) for this variety is available.</div>
            </div>
          </label>
          <Input
            label="Overseas reference application"
            defaultValue={app.overseas_reference_application || ''}
            onBlur={e => patch({ overseas_reference_application: e.target.value })}
            placeholder="e.g., US PP12345 or UK PVR 2016/100"
          />
          <div className={`p-3 rounded border text-sm flex items-start gap-2 ${app.growing_trial_required ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
            {app.growing_trial_required ? <AlertCircle className="h-4 w-4 mt-0.5" /> : <CheckCircle2 className="h-4 w-4 mt-0.5" />}
            <div>
              <div className="font-medium">
                {app.growing_trial_required ? 'Comparative growing trial required' : 'No growing trial required'}
              </div>
              {app.growing_trial_waiver_reason && <div className="text-xs mt-1">{app.growing_trial_waiver_reason}</div>}
            </div>
          </div>
        </section>

        {/* Part 1 builder */}
        <section className="bg-white border border-neutral-200 rounded-xl p-6 space-y-3">
          <h2 className="text-lg font-semibold">Part 1 Builder</h2>
          <p className="text-sm text-neutral-600">
            Fields auto-populated from the cultivar: botanical name, first-sale dates, US patent.
            Applicant / Agent / QP sections fill from the fields above. Edit the cultivar itself to
            update cultivar-level fields.
          </p>
          <div className="bg-neutral-50 rounded p-3 text-sm space-y-1">
            <div><span className="text-neutral-500">Botanical:</span> {app.botanical_name || '—'}</div>
            <div><span className="text-neutral-500">First sale AU:</span> {app.first_sale_date_au || '—'}</div>
            <div><span className="text-neutral-500">First sale overseas:</span> {app.first_sale_date_overseas || '—'} {app.first_sale_country_overseas ? `(${app.first_sale_country_overseas})` : ''}</div>
            <div><span className="text-neutral-500">US patent:</span> {app.us_patent_number || '—'}</div>
            <div><span className="text-neutral-500">Breeder:</span> {app.breeder_name || '—'}</div>
          </div>
          <Button size="sm" variant="ghost" onClick={() => generate('part1')} disabled={generating === 'part1' || saving}>
            <Download className="h-4 w-4 mr-1" />
            {generating === 'part1' ? 'Generating…' : 'Generate Part 1 PDF'}
          </Button>
        </section>

        {/* Documents */}
        <section className="bg-white border border-neutral-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-4">Documents</h2>
          {docs.length === 0 ? (
            <p className="text-sm text-neutral-500">No documents yet.</p>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {docs.map(d => (
                <li key={d.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-medium">{d.title}</div>
                    <div className="text-xs text-neutral-500">{d.document_type} · {d.created_at.slice(0, 10)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Timeline */}
        <section className="bg-white border border-neutral-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-4">Timeline</h2>
          {events.length === 0 ? (
            <p className="text-sm text-neutral-500">No events yet.</p>
          ) : (
            <ol className="space-y-3">
              {events.map(e => (
                <li key={e.id} className="flex items-start gap-3 text-sm">
                  <div className="min-w-[90px] text-neutral-500 text-xs">{e.event_date}</div>
                  <div>
                    <span className="font-medium">{e.event_type}</span>
                    {e.description && <span className="text-neutral-600"> — {e.description}</span>}
                    {e.created_by_username && <span className="text-neutral-400 text-xs ml-2">by {e.created_by_username}</span>}
                  </div>
                </li>
              ))}
            </ol>
          )}
          <div className="mt-4 pt-4 border-t border-neutral-100">
            <NoteInput appId={appId} onAdded={load} />
          </div>
        </section>

        {/* Notes */}
        <section className="bg-white border border-neutral-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-2">Notes</h2>
          <textarea
            defaultValue={app.notes || ''}
            onBlur={e => patch({ notes: e.target.value })}
            rows={4}
            className="input-professional w-full"
          />
        </section>

        <div className="flex justify-between">
          <Button variant="ghost" onClick={() => navigate('/cultivar/pbr/applications')}>
            Back to applications
          </Button>
          {saving && <span className="text-sm text-neutral-500 flex items-center gap-1"><Save className="h-3 w-3" /> Saving…</span>}
        </div>
      </div>
    </div>
  );
}

function NoteInput({ appId, onAdded }: { appId: number; onAdded: () => void }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!text.trim()) return;
    setSending(true);
    const r = await pbrService.addEvent(appId, { event_type: 'note', description: text });
    setSending(false);
    if (r.success) { setText(''); onAdded(); }
  };

  return (
    <div className="flex items-start gap-2">
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        rows={2}
        placeholder="Add a note to the timeline…"
        className="input-professional flex-1"
      />
      <Button onClick={submit} disabled={sending || !text.trim()}>
        <Send className="h-4 w-4 mr-1" /> Add
      </Button>
    </div>
  );
}
